import React from 'react';
import i18next from 'i18next';
import {faPrint} from '@fortawesome/free-solid-svg-icons';
import {lingua} from '../../preferenze';
import {valuta} from '../../skin';
import {formattaImporto} from '../../utils/formato';
import {testoPrenotazione, copiaTesto} from '../../utils/condividi';
import {paginaPrenotazione} from '../../utils/stampaGiocata';
import {stampaPagina} from '../../utils/finestraDiStampa';
import {creaImmaginePrenotazione} from '../../utils/immagineGiocata';
import FoglioCondividi from '../Condivisione/FoglioCondividi';
import IconaFa from '../common/IconaFa';

/* Gli importi nella valuta della skin, come in tutto il sito: mai «€» fisso. */
const soldi = (n) => formattaImporto(Number(n || 0), {valuta: valuta()});

/** «martedì 7 ottobre alle 20:45», nel fuso di chi guarda. */
function quando(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const giorno = d.toLocaleDateString(lingua(), { weekday: 'long', day: 'numeric', month: 'long' });
  const ora = d.toLocaleTimeString(lingua(), { hour: '2-digit', minute: '2-digit' });
  return `${giorno} alle ${ora}`;
}

/**
 * LA GIOCATA PRENOTATA.
 *
 * Un codice serve a poco se non lo puoi mandare a qualcuno: per questo la
 * parte grossa di questa maschera non e' il codice, sono i modi di mandarlo.
 *
 * I MODI LI DECIDE IL BACKOFFICE (Impostazioni schedina › Stampa e
 * condivisione), con le regole di chi guarda (`regole`): i canali accesi
 * sono tutti in vista, sempre, e il foglio del telefono e' uno di loro
 * («Altro…»). Prima sul telefono c'era solo «Condividi», e chi non sapeva
 * che li' dentro c'era WhatsApp non lo trovava. Con le regole arrivano anche
 * l'immagine della prenotazione e la stampa vera (rotolo o A4, come dice il
 * backoffice) al posto della stampa di tutta la pagina.
 *
 * SENZA REGOLE (un servizio di prima) la maschera e' quella di sempre: sul
 * telefono «Condividi», sul monitor copia, WhatsApp, Telegram, posta.
 *
 * COSA FA
 *   Mostra il codice, i modi per mandarlo, cosa c'e' dentro la giocata, da
 *   quando a quando vale, e come si gioca subito. Il messaggio che parte
 *   porta l'importo, la vincita, il codice e un collegamento che riapre la
 *   schedina gia' compilata sul sito dove e' nata.
 *
 * COSA NON FA
 *   Non impegna il saldo di nessuno: chi riceve il collegamento la gioca e
 *   paga lui. Non blocca le quote — vale il prezzo del momento in cui si
 *   gioca, ed e' scritto qui sopra invece che scoperto dopo. E nel messaggio
 *   non ci va niente di personale: non chi l'ha fatta, non il conto, non il
 *   saldo, perche' quel messaggio finisce in una chat di gruppo il giorno
 *   dopo averlo mandato.
 *
 * QUANDO NON RIESCE
 *   `pronta: false` disegna la stessa maschera senza codice e senza modi di
 *   mandarlo, col motivo scritto dentro — il codice dell'errore e il
 *   dettaglio, come ovunque. Un codice finto sarebbe peggio di nessun codice.
 */
export default function Prenotazione({
  /* { pronta, codice, scadeIl, scadenzaPerche, link, motivo, riepilogo,
       e quello che serve a messaggio, carta e immagine: selezioni, tipo,
       creataIl, importo, vincitaVista, primoEvento } */
  stato,
  /* Il riepilogo della giocata, per la riga che dice cosa c'e' dentro. */
  quante,
  tipo,
  importo,
  vincita,
  /* Le regole di stampa e condivisione di chi guarda, o null (vedi sopra). */
  regole = null,
  /* Le regole dell'agenzia sono in lettura: niente tasti finche' non
     arrivano, invece dei tasti di prima per un attimo. */
  regoleInArrivo = false,
  onChiudi,
}) {
  const pronta = Boolean(stato && stato.pronta && stato.codice);
  const codice = pronta ? stato.codice : null;
  const link = pronta ? stato.link : null;
  const [copiato, setCopiato] = React.useState(false);
  const [stampa, setStampa] = React.useState({inCorso: false, errore: null});

  /* Il foglio del telefono esiste solo dove il browser lo offre. Dove non
     c'e', restano i modi scritti: non si indovina cosa ha installato chi
     guarda. */
  const puoCondividere = typeof navigator !== 'undefined'
      && typeof navigator.share === 'function';

  /* LA PRENOTAZIONE COME LA LEGGONO MESSAGGIO, CARTA E IMMAGINE: quello che
     la schedina ha tenuto (selezioni, scadenza, importo proposto) e, dove
     manca, il riepilogo della schedina che intanto si e' svuotata. */
  const prenotazione = React.useMemo(() => (pronta ? {
    ...stato,
    riepilogo: stato.riepilogo || {quante, tipo, importo, vincita},
  } : null), [pronta, stato, quante, tipo, importo, vincita]);

  /* IL MESSAGGIO porta l'importo proposto e la vincita: sono la proposta
     stessa, e la regola che nasconde gli importi qui non vale. */
  const messaggio = React.useMemo(
    () => (prenotazione ? testoPrenotazione({prenotazione, link}) : ''),
    [prenotazione, link]);

  function condividi() {
    if (!pronta) return;
    navigator.share({ title: i18next.t('Giocata prenotata'), text: messaggio })
        .catch(() => { /* chiuso dall'utente: non e' un errore */ });
  }

  /* Copia: navigator.clipboard e, dove manca o dice di no (una pagina non
     https), la casella nascosta. */
  function copia() {
    if (!pronta) return;
    copiaTesto(messaggio).then((fatto) => {
      if (!fatto) return;
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    });
  }

  /* LA STAMPA DELLA PRENOTAZIONE: il suo foglio, sulla carta che dice il
     backoffice. Il logo, sull'A4 come sul rotolo, c'e' se la regola non dice
     MAI (qui non c'e' la casella «senza logo»). Le
     prenotazioni non si contano: non sono giocate. */
  async function stampaPrenotazione() {
    if (!pronta || !regole || stampa.inCorso) return;
    const formato = regole.formato || 'A4';
    setStampa({inCorso: true, errore: null});
    try {
      await stampaPagina(paginaPrenotazione({prenotazione, formato, conLogo: regole.logo !== 'MAI'}), {formato});
      setStampa({inCorso: false, errore: null});
    } catch (e) {
      setStampa({inCorso: false, errore: i18next.t('La stampa non è partita: riprova.')});
    }
  }

  /* I collegamenti di WhatsApp e Telegram sono indirizzi pubblici che
     aprono l'applicazione se c'e' e il sito se non c'e': niente da
     installare, niente da indovinare. */
  const testo = encodeURIComponent(messaggio);
  const whatsapp = `https://wa.me/?text=${testo}`;
  const telegram = `https://t.me/share/url?url=${encodeURIComponent(link || '')}`
      + `&text=${encodeURIComponent(messaggio.replace(link || '', '').trim())}`;
  const posta = `mailto:?subject=${encodeURIComponent(i18next.t('Giocata prenotata'))}&body=${testo}`;

  const scadenza = stato && stato.scadeIl ? quando(stato.scadeIl) : '';
  const perche = stato && stato.scadenzaPerche === 'PRIMO_EVENTO'
    ? i18next.t(' — comincia la prima partita')
    : '';

  return (
    <div className="lvc-sched-prenotata">
      <div className="lvc-sched-testata">
        <span>{i18next.t('Giocata prenotata')}</span>
        <span className="lvc-sched-testata-dx">
          <button type="button" className="lvc-sched-torna" onClick={onChiudi}>
            {i18next.t('Torna alla schedina')}
          </button>
        </span>
      </div>

      <div className="lvc-sched-soldi">
        {pronta ? (
          <div className="lvc-sched-codice-grande">
            <span className="k">{i18next.t('Codice')}</span>
            <span className="v">{codice}</span>
          </div>
        ) : (
          /* IN VISTA, COL MOTIVO SCRITTO QUI DENTRO: codice e dettaglio. */
          <div className="lvc-sched-manca">
            <b>{i18next.t('Non è stata prenotata')}</b>
            {i18next.t('Il servizio ha detto di no, e il motivo è questo:')}
            {stato && stato.motivo && (
              <span className="codice-errore">{stato.motivo}</span>
            )}
          </div>
        )}

        {/* I MODI DI MANDARLO. Con le regole: i canali accesi, tutti in
            vista. Senza (servizio di prima): sul telefono uno, sul monitor
            quattro. */}
        {regoleInArrivo ? null : regole ? (
          pronta && regole.condivisionePrenotazione === true ? (
            <FoglioCondividi
              forma="dentro"
              titolo={i18next.t('Mandala con')}
              regole={regole}
              importi="VISIBILI"
              testo={() => messaggio}
              link={link || ''}
              codice={codice}
              preparaImmagine={() => creaImmaginePrenotazione(prenotazione)}
              nomeFile={`prenotazione-${codice}.png`}
              cosa="prenotazione"
              oggetto={i18next.t('Giocata prenotata')}
            />
          ) : null
        ) : puoCondividere ? (
          <button
            type="button"
            className="lvc-sched-conferma"
            disabled={!pronta}
            onClick={condividi}
          >
            {i18next.t('Condividi')}
          </button>
        ) : (
          <div className="lvc-sched-manda">
            <button type="button" disabled={!pronta} onClick={copia}>
              {copiato ? i18next.t('Copiato') : i18next.t('Copia')}
            </button>
            <a className={pronta ? '' : 'spento'} href={pronta ? whatsapp : undefined}
              aria-disabled={pronta ? undefined : 'true'}
              target="_blank" rel="noopener noreferrer">{i18next.t('WhatsApp')}</a>
            <a className={pronta ? '' : 'spento'} href={pronta ? telegram : undefined}
              aria-disabled={pronta ? undefined : 'true'}
              target="_blank" rel="noopener noreferrer">{i18next.t('Telegram')}</a>
            <a className={pronta ? '' : 'spento'} href={pronta ? posta : undefined}
              aria-disabled={pronta ? undefined : 'true'}>{i18next.t('Posta')}</a>
          </div>
        )}

        {/* COSA C'E' DENTRO, DA QUANDO A QUANDO VALE. Le tre righe stanno qui
            e non sulla ricevuta: servono prima di mandare il messaggio, non
            dopo. */}
        <p className="lvc-sched-punto">
          <s style={{ color: 'var(--lvc-accent-deep)' }}>&#9679;</s>
          {quante === 1 ? i18next.t('1 selezione') : i18next.t('{{n}} selezioni', {n: quante})}
          {' '}&middot; {tipo}
          {' '}&middot; {i18next.t('{{importo}} · vincita {{vincita}}', {importo: soldi(importo), vincita: soldi(vincita)})}
        </p>
        <p className="lvc-sched-punto">
          <s style={{ color: 'var(--lvc-warning)' }}>&#9679;</s>
          {i18next.t('Le quote non sono bloccate: vale il prezzo del momento in cui si gioca.')}
        </p>
        <p className="lvc-sched-punto">
          <s style={{ color: 'var(--lvc-text-4)' }}>&#9679;</s>
          {scadenza
            ? <>{i18next.t('Vale fino a')}{' '}{scadenza}{perche}.</>
            : <>{i18next.t('Scade all’inizio della prima partita.')}</>}
        </p>
        {pronta && link && (
          <p className="lvc-sched-punto">
            <s style={{ color: 'var(--lvc-text-4)' }}>&#9679;</s>
            {i18next.t('Chi apre il collegamento trova la schedina già compilata, e gioca con il suo conto.')}
          </p>
        )}

        <button type="button" className="lvc-sched-conferma" onClick={onChiudi}>
          {i18next.t('Entra e gioca ora')}
        </button>
        {regoleInArrivo ? null : regole ? (
          pronta && regole.stampaPrenotazione === true ? (
            <>
              <button type="button" className="lvc-sched-seconda" disabled={stampa.inCorso}
                onClick={stampaPrenotazione}
                style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
                <IconaFa icon={faPrint} style={{width: '1em', height: '1em'}}/>
                {stampa.inCorso ? i18next.t('Un attimo…') : i18next.t('Stampa la prenotazione')}
              </button>
              <p role="status" style={{
                margin: stampa.errore ? '8px 0 0' : 0, fontSize: '12px', lineHeight: 1.45,
                color: 'var(--lvc-negative)',
              }}>
                {stampa.errore || null}
              </p>
            </>
          ) : null
        ) : (
          <button type="button" className="lvc-sched-seconda" disabled={!pronta}
            onClick={() => window.print()}>
            {i18next.t('Stampa')}
          </button>
        )}
      </div>
    </div>
  );
}
